import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import logoCYT from "/images/logo-black.png";

/**
 * OAuth Callback Page
 *
 * This page is used as the redirect_uri for the popup OAuth flow.
 * It reads the authorization code (or error) from the URL and posts
 * it back to the opener window via postMessage, then closes itself.
 *
 * The `origin` query parameter is used as the targetOrigin for postMessage
 * so that the authorization code is only delivered to the expected third-party
 * domain. The server validates this origin when issuing the code.
 *
 * Third-party apps using the CYT Auth SDK set their redirect_uri to
 * this page. The SDK listens for the postMessage on the opener.
 */
export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const error = searchParams.get("error");
  const legacyOrigin = searchParams.get("origin");

  // Caller origin is encoded in the state by the SDK (base64 JSON {n, o}).
  // Fall back to legacy ?origin= param. Null means origin is unknown.
  const callerOrigin = useMemo(() => {
    if (state) {
      try {
        const decoded = JSON.parse(atob(state));
        if (decoded?.o) return decoded.o;
      } catch (_) {}
    }
    return legacyOrigin || null;
  }, [state, legacyOrigin]);

  useEffect(() => {
    const message = {
      type: "cyt-oauth-callback",
      code: code || null,
      state: state || null,
      error: error || null,
    };

    // Post the result back to the opener window
    if (window.opener && callerOrigin) {
      window.opener.postMessage(message, callerOrigin);
      setStatus(code ? "success" : "error");

      // Close the popup after a short delay so the user sees the status
      setTimeout(() => {
        window.close();
      }, 1500);
    } else {
      setStatus(code ? "success" : "error");
    }
  }, [code, state, error, callerOrigin]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-sm text-center">
        <CardHeader>
          <div className="mx-auto mb-4">
            <img src={logoCYT} alt="Casas y Terrenos" className="h-8" />
          </div>

          {status === "processing" && <CardTitle className="text-lg">Procesando...</CardTitle>}

          {status === "success" && (
            <>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle className="text-lg">Autorización exitosa</CardTitle>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-lg">
                {error === "access_denied" ? "Acceso denegado" : "Error de autorización"}
              </CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            {window.opener && callerOrigin
              ? "Esta ventana se cerrará automáticamente."
              : "Puedes cerrar esta ventana."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
